import { useState } from "react";

import { Building2, ChevronDown } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useWorkspaces } from "../hooks";

import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

interface WorkspaceSwitcherButtonProps {
  isStreaming?: boolean;
  isLoading?: boolean;
  hasIncompleteAttachments?: boolean;
  isCollapsed?: boolean;
}

export const WorkspaceSwitcherButton = ({
  isStreaming = false,
  isLoading = false,
  hasIncompleteAttachments = false,
  isCollapsed = false,
}: WorkspaceSwitcherButtonProps) => {
  const [isWorkspaceSwitcherOpen, setIsWorkspaceSwitcherOpen] = useState(false);
  const { data: workspacesData, isLoading: isLoadingWorkspaces } =
    useWorkspaces();

  const activeWorkspace = workspacesData?.workspaces?.find((w) => w.active);
  const isDisabled = isStreaming || isLoading || hasIncompleteAttachments;

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleClick = () => {
    if (!isDisabled) {
      setIsWorkspaceSwitcherOpen(true);
    }
  };

  const tooltipText =
    isStreaming || isLoading
      ? "Workspace switching disabled during processing..."
      : hasIncompleteAttachments
        ? "Complete file uploads before switching workspace"
        : activeWorkspace
          ? `Current workspace: ${activeWorkspace.name}`
          : "Switch workspace";

  if (isLoadingWorkspaces) {
    if (isCollapsed) {
      return (
        <div className="flex justify-center">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center">
            <div className="w-6 h-6 rounded-md bg-muted-foreground/20 animate-pulse" />
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="px-2">
          <div className="w-full h-9 rounded-lg flex items-center gap-2 px-2">
            <div className="w-6 h-6 rounded-md bg-muted-foreground/20 animate-pulse flex-shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-2 bg-muted-foreground/20 rounded animate-pulse w-14" />
              <div className="h-3 bg-muted-foreground/20 rounded animate-pulse w-24" />
            </div>
          </div>
        </div>
        <div className="mx-2 my-4 border-t border-sidebar-border" />
      </>
    );
  }

  if (workspacesData?.workspaces?.length === 0) {
    return null;
  }

  if (isCollapsed) {
    return (
      <>
        <div className="flex justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleClick}
                  disabled={isDisabled}
                  aria-label={tooltipText}
                  data-testid="workspace-switcher-button"
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                    isDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-white/10"
                  }`}
                >
                  <div className="w-6 h-6 rounded-md bg-primary bg-[image:var(--primary-gradient-avatar,none)] flex items-center justify-center text-primary-foreground text-[10px] font-semibold">
                    {activeWorkspace ? (
                      getInitials(activeWorkspace.name)
                    ) : (
                      <Building2 size={14} />
                    )}
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <WorkspaceSwitcher
          isOpen={isWorkspaceSwitcherOpen}
          onClose={() => setIsWorkspaceSwitcherOpen(false)}
        />
      </>
    );
  }

  return (
    <>
      <div className="px-2">
        <button
          onClick={handleClick}
          disabled={isDisabled}
          title={tooltipText}
          data-testid="workspace-switcher-button"
          className={`w-full h-9 rounded-lg flex items-center gap-2 px-2 transition-colors ${
            isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/10"
          }`}
        >
          <div className="w-6 h-6 rounded-md bg-primary bg-[image:var(--primary-gradient-avatar,none)] flex items-center justify-center text-primary-foreground text-[10px] font-semibold flex-shrink-0">
            {activeWorkspace ? (
              getInitials(activeWorkspace.name)
            ) : (
              <Building2 size={14} />
            )}
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="truncate text-sm font-semibold leading-tight text-sidebar-foreground">
              {activeWorkspace?.name || "Select workspace"}
            </div>
            <div className="truncate text-xs leading-tight text-sidebar-foreground/60">
              Workspace
            </div>
          </div>
          <ChevronDown
            size={14}
            className="flex-shrink-0 text-sidebar-foreground/40"
          />
        </button>
      </div>
      <div className="mx-2 my-4 border-t border-sidebar-border" />
      <WorkspaceSwitcher
        isOpen={isWorkspaceSwitcherOpen}
        onClose={() => setIsWorkspaceSwitcherOpen(false)}
      />
    </>
  );
};
